import { useState, useEffect } from 'react';
import { Home, LayoutGrid, ShoppingBag, Heart, User } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { useCart } from '@/context/CartContext';
import { useFavorites } from '@/context/FavoritesContext';
import { useCustomer } from '@/context/CustomerContext';
import { switchToPanel, PANEL_EVENT, BOTTOM_NAV_OVERRIDE_EVENT, type MobilePanel } from '@/lib/mobilePanels';

const GOLD = '#b83f74';
const DARK = '#2a1f14';

function Badge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      style={{
        position: 'absolute', top: 2, right: '50%', marginRight: -18,
        minWidth: 15, height: 15, borderRadius: 8, padding: '0 3px',
        background: GOLD, color: '#fff',
        fontSize: 9, fontWeight: 700, lineHeight: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'DM Sans, sans-serif',
      }}
    >
      {count}
    </span>
  );
}

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 9.5, letterSpacing: '0.04em',
  lineHeight: 1, marginTop: 3,
};

/**
 * Fixed bottom navigation — mobile only.
 * Start | Kategorie | Koszyk (highlighted) | Ulubione | Konto
 */
export function MobileBottomNav() {
  const { totalCount } = useCart();
  const { favorites } = useFavorites();
  const { customer, ensureSession } = useCustomer();
  const [location, setLocation] = useLocation();
  const [activePanel, setActivePanel] = useState<MobilePanel | null>(null);
  const [navHidden, setNavHidden] = useState(false);

  // Track which panel is open for active-tab highlighting
  useEffect(() => {
    const handler = (e: Event) => {
      const panel = (e as CustomEvent<MobilePanel>).detail;
      setActivePanel(panel);
    };
    window.addEventListener(PANEL_EVENT, handler);
    return () => window.removeEventListener(PANEL_EVENT, handler);
  }, []);

  // Strona produktu ma własny, sticky pasek CTA na dole ekranu (mobile) —
  // dopóki on jest widoczny, ten nav w ogóle się nie montuje, żeby nigdy nie
  // istniały jednocześnie dwa niezależne fixed paski przyklejone do dołu
  // ekranu (patrz komentarz przy BOTTOM_NAV_OVERRIDE_EVENT w mobilePanels.ts).
  useEffect(() => {
    const handler = (e: Event) => {
      setNavHidden((e as CustomEvent<boolean>).detail);
    };
    window.addEventListener(BOTTOM_NAV_OVERRIDE_EVENT, handler);
    return () => window.removeEventListener(BOTTOM_NAV_OVERRIDE_EVENT, handler);
  }, []);

  const itemClass = 'relative flex flex-col items-center justify-center flex-1 min-h-[62px]';
  const tabColor = (panel: MobilePanel | 'home') => {
    if (panel === 'home') return !activePanel && location === '/' ? GOLD : '#6b6560';
    return activePanel === panel ? GOLD : '#6b6560';
  };

  if (navHidden) return null;

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 w-full z-[100] md:hidden"
        data-testid="mobile-bottom-nav"
        aria-label="Nawigacja dolna"
        style={{
          background: '#fff',
          borderTop: '1px solid rgba(210,60,120,0.25)',
          boxShadow: '0 -2px 14px rgba(42,31,20,0.06)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          // Force GPU compositing layer — keeps nav fixed through iOS address-bar
          // show/hide cycles and back-navigation paint cycles.
          transform: 'translateZ(0)',
        }}
      >
        {/* Safari iOS bottom bleed mask — extends 100px below the sticky nav */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: -100,
            background: '#ffffff',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
        <div style={{ display: 'flex', alignItems: 'stretch', position: 'relative', zIndex: 1 }}>
          {/* Start — closes all panels then navigates home */}
          <Link
            href="/"
            className={itemClass}
            data-testid="bottomnav-start"
            onClick={() => { switchToPanel(null); setActivePanel(null); }}
            style={{ textDecoration: 'none', color: tabColor('home') }}
          >
            <Home size={21} strokeWidth={1.6} />
            <span style={LABEL_STYLE}>Start</span>
          </Link>

          {/* Kategorie — toggles category drawer (closes all others first) */}
          <button
            className={itemClass}
            data-testid="bottomnav-kategorie"
            aria-label="Kategorie"
            aria-expanded={activePanel === 'categories'}
            onClick={() => switchToPanel(activePanel === 'categories' ? null : 'categories')}
            style={{ background: 'none', border: 'none', color: tabColor('categories'), cursor: 'pointer' }}
          >
            <LayoutGrid size={21} strokeWidth={1.6} />
            <span style={LABEL_STYLE}>Kategorie</span>
          </button>

          {/* Koszyk — highlighted, centre */}
          <button
            className="relative flex flex-col items-center justify-center flex-1"
            data-testid="bottomnav-koszyk"
            aria-label="Koszyk"
            aria-expanded={activePanel === 'cart'}
            onClick={() => switchToPanel(activePanel === 'cart' ? null : 'cart')}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <span
              style={{
                width: 46, height: 46, borderRadius: '50%',
                background: GOLD, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginTop: -16,
                boxShadow: '0 4px 14px rgba(210,60,120,0.45)',
                border: '3px solid #fff',
                position: 'relative',
              }}
            >
              <ShoppingBag size={20} strokeWidth={1.7} />
              {totalCount > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  minWidth: 16, height: 16, borderRadius: 8, padding: '0 3px',
                  background: DARK, color: '#fff',
                  fontSize: 9, fontWeight: 700, lineHeight: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'DM Sans, sans-serif',
                }}>
                  {totalCount}
                </span>
              )}
            </span>
            <span style={{ ...LABEL_STYLE, color: '#6b6560' }}>Koszyk</span>
          </button>

          {/* Ulubione */}
          <button
            className={itemClass}
            data-testid="bottomnav-ulubione"
            aria-label="Ulubione"
            aria-expanded={activePanel === 'favorites'}
            onClick={() => switchToPanel(activePanel === 'favorites' ? null : 'favorites')}
            style={{ background: 'none', border: 'none', color: tabColor('favorites'), cursor: 'pointer' }}
          >
            <Heart size={21} strokeWidth={1.6} fill={activePanel === 'favorites' ? GOLD : 'none'} />
            <Badge count={favorites.length} />
            <span style={LABEL_STYLE}>Ulubione</span>
          </button>

          {/* Konto — official Shopify launcher for signed-out mobile users */}
          {customer ? (
            <button
              className={itemClass}
              data-testid="bottomnav-konto"
              aria-label="Konto"
              onClick={() => setLocation('/konto')}
              style={{ background: 'none', border: 'none', color: tabColor('account'), cursor: 'pointer' }}
            >
              <User size={21} strokeWidth={1.6} />
              <span style={LABEL_STYLE}>Konto</span>
            </button>
          ) : (
            <a
              className={itemClass}
              href={`${import.meta.env.BASE_URL.replace(/\/$/, '')}/api/auth/google/start`}
              data-testid="shopify-account-launcher-mobile"
              aria-label="Konto — zaloguj się"
              onClick={event => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                const target = event.currentTarget.href;
                void ensureSession().then(session => {
                  if (session.customer) setLocation('/konto');
                  else window.location.assign(target);
                });
              }}
              style={{ textDecoration: 'none', color: tabColor('account'), cursor: 'pointer' }}
            >
              <User size={21} strokeWidth={1.6} />
              <span style={LABEL_STYLE}>Konto</span>
            </a>
          )}
        </div>

      </nav>
    </>
  );
}
